import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, VStack, useToast } from "@chakra-ui/react";
import { MegaphoneIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import AudioCard from "../../components/cards/AudioCard";
import EmptyState from "../../components/placeholders/EmptyState";
import CustomLoader from "../../components/placeholders/CustomLoader";
import { useRecordings } from "../../context/RecordingContext";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import SearchBar from "../../components/UI/SearchBar";
import { Recording } from "../../model/recording";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";

const PAGE_SIZE = 10;

const ExhortationPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    hasInitialized,
    setHasInitialized,
    recordingsByCategory,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    listRecordingsByCategory,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const {
    searchMode,
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  } = useSharedRecordingQuery({
    matchRecording: (recording: Recording) =>
      recording.category === "exhortation",
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.exhortations.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const recordings = recordingsByCategory.exhortation ?? [];

  const loadRecordingsPage = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setIsFetching(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await listRecordingsByCategory("exhortation", {
          limit: PAGE_SIZE,
          nextPageToken: reset ? undefined : nextPageToken,
        });
        setNextPageToken(result.nextPageToken);
      } finally {
        setIsFetching(false);
        setIsLoadingMore(false);
      }
    },
    [listRecordingsByCategory, nextPageToken],
  );

  useEffect(() => {
    if (hasInitialized.exhortation) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        if (!hasLoadedFavoriteIDs) {
          await listFavoriteIDs();
        }

        if (!hasLoadedCategory("exhortation")) {
          await loadRecordingsPage(true);
        }
      } catch (error) {
        toast({
          title: t("pages.exhortations.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, exhortation: true }));
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.exhortation,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    loadRecordingsPage,
    setHasInitialized,
    t,
    toast,
  ]);

  const displayedRecordings = useMemo(
    () => (sharedRecording ? [sharedRecording] : recordings),
    [recordings, sharedRecording],
  );

  const hasNoData = !sharedRecording && recordings.length === 0;

  const handleLoadMore = async () => {
    if (!nextPageToken || isLoadingMore) {
      return;
    }

    try {
      await loadRecordingsPage(false);
    } catch (error) {
      toast({
        title: t("pages.exhortations.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <SearchBar
        value={searchQuery}
        onSearch={setSearchQuery}
        placeholder={String(t("pages.exhortations.searchPlaceholder"))}
        mode={searchMode}
        mb={7}
        onSuggestionSelect={(item) => selectSharedRecording(item.recordingID)}
      />
      {isFetching || isLoadingSharedRecording || !hasInitialized.exhortation ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={MegaphoneIcon}
          title={t("pages.exhortations.emptyTitle")}
          description={t("pages.exhortations.emptyDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(recording.date)}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}

          {!sharedRecording && nextPageToken && (
            <Button
              mt={2}
              alignSelf="center"
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
            >
              {t("common.loadMore")}
            </Button>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default ExhortationPage;
